import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

const inputCls =
  "w-full bg-transparent border-b border-[#1A1A1A]/25 py-3 text-base font-light placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#4A148C] transition-colors duration-300";

export default function Trade() {
  const [token, setToken] = useState(() => sessionStorage.getItem("proppa_trade_token") || "");
  const [business, setBusiness] = useState(() => sessionStorage.getItem("proppa_trade_business") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/trade/prices`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setPrices(res.data))
      .catch(() => {
        sessionStorage.removeItem("proppa_trade_token");
        sessionStorage.removeItem("proppa_trade_business");
        setToken("");
      });
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/trade/login`, login);
      sessionStorage.setItem("proppa_trade_token", res.data.token);
      sessionStorage.setItem("proppa_trade_business", res.data.business_name);
      setBusiness(res.data.business_name);
      setToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
  };

  const signOut = () => {
    sessionStorage.removeItem("proppa_trade_token");
    sessionStorage.removeItem("proppa_trade_business");
    setToken("");
    setPrices(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#FDFBF7]" data-testid="trade-login-page">
        <form onSubmit={doLogin} className="max-w-sm w-full border border-[#E5DFD3] p-10" data-testid="trade-login-form">
          <p className="font-serif text-3xl font-light tracking-tight">
            Proppa <span className="italic text-[#4A148C]">Foods</span> <span className="text-sm text-[#6E6B65]">trade</span>
          </p>
          <p className="mt-4 text-sm font-light text-[#1A1A1A]/60">
            Trade accounts are for pubs, takeaways and shops. Need one? Send a wholesale enquiry first.
          </p>
          <div className="mt-8 space-y-6">
            <input type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="trade-email-input" />
            <input type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} placeholder="Password" className={inputCls} data-testid="trade-password-input" />
          </div>
          {error && <p className="mt-4 text-xs text-[#4A148C]" data-testid="trade-login-error">{error}</p>}
          <button type="submit" data-testid="trade-login-button" className="mt-8 w-full bg-[#1A1A1A] text-[#FDFBF7] px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] transition-colors duration-300">
            Sign in
          </button>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs text-[#1A1A1A]/50 hover:text-[#4A148C] transition-colors" data-testid="trade-back-link">
            <ArrowLeft size={12} /> Back to shop
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6" data-testid="trade-price-page">
      <div className="max-w-[900px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#4A148C] mb-3">Trade only</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter">
              Price list — <span className="italic text-[#4A148C]">{business}</span>
            </h1>
          </div>
          <button onClick={signOut} data-testid="trade-signout-button" className="inline-flex items-center gap-2 text-xs text-[#1A1A1A]/50 hover:text-[#4A148C] transition-colors">
            <LogOut size={12} /> Sign out
          </button>
        </div>
        <p className="text-sm font-light text-[#1A1A1A]/60 mb-12 max-w-lg">
          Order by email to <span className="font-medium text-[#1A1A1A]">orders@proppafoods.com</span> quoting
          your business name. Free delivery over £30 as usual; dip pots minimum 6.
        </p>

        {!prices ? (
          <p className="text-sm font-light text-[#1A1A1A]/50">Loading your prices…</p>
        ) : (
          <div className="border-t border-[#E5DFD3]" data-testid="trade-price-list">
            {prices.map((p) => (
              <div key={p.lookup_key} className="flex items-baseline justify-between gap-6 py-5 border-b border-[#E5DFD3]" data-testid={`trade-price-${p.lookup_key}`}>
                <div>
                  <p className="font-serif text-xl">{p.name}</p>
                  <p className="text-xs font-light text-[#1A1A1A]/50 mt-1">{p.strap}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-light text-[#1A1A1A]/40 line-through">{gbp(p.price_pence)}</p>
                  <p className="font-serif text-2xl text-[#4A148C]">{gbp(p.trade_price_pence ?? p.price_pence)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-10 text-xs font-light text-[#1A1A1A]/40">
          Prices are per case/pack as listed and may change — confirmed on every order.
        </p>
      </div>
    </div>
  );
}
